import api from './api';

// Get all products with optional filters and pagination
export const getProducts = async (params = {}) => {
    const queryParams = new URLSearchParams();

    // Add filtering parameters
    if (params.category) {
        queryParams.append('category', params.category);
    }

    if (params.subcategory) {
        queryParams.append('subcategory', params.subcategory);
    }

    if (params.tags) {
        const tagsArray = Array.isArray(params.tags) ? params.tags : [params.tags];
        queryParams.append('tags', tagsArray.join(','));
    }

    if (params.search) {
        queryParams.append('search', params.search);
    }

    if (params.brand) {
        queryParams.append('brand', params.brand);
    }

    if (params.minPrice) {
        queryParams.append('price[gte]', params.minPrice);
    }

    if (params.maxPrice) {
        queryParams.append('price[lte]', params.maxPrice);
    }

    if (params.minRating) {
        queryParams.append('rating[gte]', params.minRating);
    }

    // Add sorting parameter
    if (params.sort) {
        queryParams.append('sort', params.sort);
    }

    // Add pagination parameters
    if (params.page) {
        queryParams.append('page', params.page);
    }

    if (params.limit) {
        queryParams.append('limit', params.limit);
    }

    // Add field selection
    if (params.select) {
        queryParams.append('select', params.select);
    }

    const url = queryParams.toString() ? `/products?${queryParams.toString()}` : '/products';
    const response = await api.get(url);
    return response.data;
};

// Get product by ID
export const getProduct = async (id) => {
    const response = await api.get(`/products/${id}`);
    return response.data;
};

// Create product (Admin/Super Admin)
export const createProduct = async (productData) => {
    const response = await api.post('/products', productData);
    return response.data;
};

// Update product (Admin/Super Admin)
export const updateProduct = async (id, productData) => {
    const response = await api.put(`/products/${id}`, productData);
    return response.data;
};

// Delete product (Admin/Super Admin)
export const deleteProduct = async (id) => {
    const response = await api.delete(`/products/${id}`);
    return response.data;
};

// Bulk delete products (Super Admin only)
export const bulkDeleteProducts = async (productIds) => {
    const response = await api.delete('/products/bulk', {
        data: { ids: productIds }
    });
    return response.data;
};

// Test bulk upload route access (Admin/Super Admin)
export const testBulkUploadAccess = async () => {
    try {
        const response = await api.post('/products/bulk-test', {
            test: 'data'
        });
        console.log('Test endpoint success:', response.data);
        return response.data;
    } catch (error) {
        console.error('Test endpoint error:', {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            message: error.message
        });
        throw error;
    }
};

// Bulk upload products (Admin/Super Admin)
export const bulkUploadProducts = async (productsData) => {
    console.log('ProductService: Making request to /products/bulk with:', {
        productsCount: productsData.length,
        requestBody: { products: productsData }
    });
    
    try {
        const response = await api.post('/products/bulk', {
            products: productsData
        });
        console.log('ProductService: Success response:', response.data);
        return response.data;
    } catch (error) {
        console.error('ProductService: Error response:', {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            headers: error.response?.headers
        });
        throw error;
    }
};

// Search products for autocomplete (returns limited results)
export const searchProducts = async (searchTerm, limit = 10) => {
    const params = new URLSearchParams();
    
    if (searchTerm) {
        params.append('search', searchTerm);
    }
    
    params.append('limit', limit);
    params.append('select', 'name,brand,_id,imageUrl');

    const url = `/products?${params.toString()}`;
    const response = await api.get(url);
    return response.data;
};

// Get category field definitions
export const getCategoryFieldDefinitions = async () => {
    const response = await api.get('/products/category-fields');
    return response.data;
};

// Smart search that returns both brands and products with categorization
export const smartSearch = async (searchTerm, limit = 8) => {
    if (!searchTerm || !searchTerm.trim()) {
        return { brands: [], products: [] };
    }

    try {
        // Search for products
        const productParams = new URLSearchParams();
        productParams.append('search', searchTerm);
        productParams.append('limit', Math.ceil(limit * 0.6)); // 60% products
        productParams.append('select', 'name,brand,_id,imageUrl,price,rating,reviewCount,category,subcategory');

        const productResponse = await api.get(`/products?${productParams.toString()}`);
        const products = productResponse.data.data || [];

        // Extract unique brands from products
        const brandMap = new Map();
        products.forEach(product => {
            if (product.brand) {
                const brandName = product.brand.toLowerCase();
                if (!brandMap.has(brandName)) {
                    brandMap.set(brandName, {
                        name: product.brand,
                        slug: product.brand.toLowerCase().replace(/[^a-z0-9]/g, '-'),
                        productCount: 1,
                        avgRating: product.rating || 0,
                        imageUrl: product.imageUrl
                    });
                } else {
                    const existingBrand = brandMap.get(brandName);
                    existingBrand.productCount += 1;
                    existingBrand.avgRating = (existingBrand.avgRating + (product.rating || 0)) / 2;
                }
            }
        });

        // Filter brands that match the search term - only at word boundaries
        const searchLower = searchTerm.toLowerCase();
        const matchingBrands = Array.from(brandMap.values())
            .filter(brand => {
                const brandLower = brand.name.toLowerCase();
                // Check if search term appears at the start of the brand name or at the start of any word
                const words = brandLower.split(/\s+/);
                return words.some(word => word.startsWith(searchLower));
            })
            .sort((a, b) => {
                const aName = a.name.toLowerCase();
                const bName = b.name.toLowerCase();
                
                // Check if brand name starts with search term
                const aStarts = aName.startsWith(searchLower);
                const bStarts = bName.startsWith(searchLower);
                
                // Check if any word in brand name starts with search term
                const aWordStarts = aName.split(/\s+/).some(word => word.startsWith(searchLower));
                const bWordStarts = bName.split(/\s+/).some(word => word.startsWith(searchLower));
                
                // Priority 1: Brand name starts with search term
                if (aStarts && !bStarts) return -1;
                if (!aStarts && bStarts) return 1;
                
                // Priority 2: Any word in brand name starts with search term
                if (aWordStarts && !bWordStarts) return -1;
                if (!aWordStarts && bWordStarts) return 1;
                
                // Otherwise alphabetical
                return aName.localeCompare(bName);
            })
            .slice(0, Math.ceil(limit * 0.4)); // 40% brands

        return {
            brands: matchingBrands,
            products: products,
            searchTerm: searchTerm
        };
    } catch (error) {
        console.error('Smart search error:', error);
        return { brands: [], products: [], searchTerm: searchTerm };
    }
};